import React, { useMemo } from "react";
import { TouchableOpacity, View } from "react-native";
import StyledText from "../../common/ui/styled-text";
import { useIsTemporarilyClosed } from "../../../hooks/useIsTemporarilyClosed";
import { useSettingsData } from "../../../contexts/SettingsDataContext";
import { useFormattedDate } from "../../../hooks/useFormattedDate";
import dayjs from "../../../utils/dayjs";
import { CalendarOff } from "lucide-react-native";

const ONE_WEEK_MINS = 7 * 24 * 60;
const ONE_DAY_MINS = 24 * 60;

export default function StoreStatus() {
  const isTempClosed = useIsTemporarilyClosed();
  const { settings, handleSaveTemporaryClosure } = useSettingsData();
  const { formatDateTimeLong, formatTime, timezone } = useFormattedDate();

  if (!isTempClosed || !settings.tempClosedUntil) return null;

  const remainingMins = dayjs(settings.tempClosedUntil)
    .tz(timezone)
    .diff(dayjs().tz(timezone), "minute");
  const remainingStr =
    remainingMins > 60
      ? `${Math.floor(remainingMins / 60)}h ${remainingMins % 60}m`
      : `${remainingMins}m`;
  const until = useMemo(() => {
    if (remainingMins > ONE_WEEK_MINS) {
      return formatDateTimeLong(settings.tempClosedUntil!);
    } else if (remainingMins > ONE_DAY_MINS) {
      return dayjs(settings.tempClosedUntil).format("dddd [at] h:mm A");
    } else {
      return formatTime(settings.tempClosedUntil!);
    }
  }, [remainingMins, settings.tempClosedUntil]);

  const handleReopenStore = () => {
    handleSaveTemporaryClosure(null, "");
  };

  return (
    <View className="border rounded-lg p-3 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
      <View className="flex flex-row items-start gap-3">
        <View className="pt-0.5">
          <CalendarOff size={18} color="#b45309" />
        </View>
        <View className="flex-1 gap-0.5">
          <StyledText className="text-sm font-semibold text-amber-800">
            Store Temporarily Closed
          </StyledText>
          <StyledText className="text-xs text-gray-600 mb-1">
            Until {until} ({remainingStr} remaining)
          </StyledText>
          <View className="self-start">
            <TouchableOpacity
              onPress={handleReopenStore}
              className="px-4 py-2 rounded-md bg-amber-600 hover:bg-amber-700 active:opacity-90 shadow-sm"
            >
              <span className="text-xs font-medium text-white">
                Reopen Store Now
              </span>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </View>
  );
}
